import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Helpers for the merchandised shop home (docs/shop/STOREFRONT_HOME.md in the
 * backend): view/click tracking for the admin's stats, the "Recently viewed"
 * store the product page writes to, and the small hooks the sections share.
 */

/* ------------------------------------------------------------ links */

/**
 * Section links are `/…` (on LK) or `https://…`. Admins sometimes paste the
 * API's `/shop/products/:slug` shape; the website's product route is
 * `/shop/p/:slug`, so map it rather than send people to a 404.
 */
export const normalizeShopLink = (link) => {
  if (!link || typeof link !== 'string') return null;
  if (/^https:\/\//i.test(link)) return { href: link };
  if (!link.startsWith('/') || link.startsWith('//')) return null;
  return { to: link.replace(/^\/shop\/products\//, '/shop/p/') };
};

/* --------------------------------------------------------- tracking */

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
const queue = [];
const seen = new Set();
let timer = null;

export const flushShopEvents = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  while (queue.length) {
    const events = queue.splice(0, 50);
    try {
      // keepalive lets the last batch survive the tab being hidden or closed.
      fetch(`${API_BASE}/shop/home/events`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
      }).catch(() => {});
    } catch {
      /* stats are best-effort */
    }
  }
};

const schedule = () => {
  if (!timer) timer = setTimeout(flushShopEvents, 5000);
};

/**
 * Queue one event. Sections from the fallback layout have no id and are not
 * tracked. Views count once per section+item per page view (see resetShopViews).
 */
export const trackShopEvent = (sectionId, itemKey, type) => {
  if (sectionId == null || !Number.isFinite(Number(sectionId))) return;
  const key = String(itemKey ?? '').slice(0, 64);
  if (type === 'view') {
    const k = `${sectionId}|${key}`;
    if (seen.has(k)) return;
    seen.add(k);
  }
  queue.push({ sectionId: Number(sectionId), itemKey: key, type });
  if (queue.length >= 50) flushShopEvents();
  else schedule();
};

/** A new visit to the home page counts its views afresh. */
export const resetShopViews = () => seen.clear();

/** Flush on page hide (tab switch, app switch, close) — the moment unload-time requests still go out. */
export const useShopEventFlush = () => {
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') flushShopEvents();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flushShopEvents);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flushShopEvents);
      flushShopEvents();
    };
  }, []);
};

/**
 * Tracks a section: every descendant carrying `data-item-key` sends a view when
 * at least half of it is on screen (IntersectionObserver clips by the
 * horizontal rails, so off-rail cards don't count), and any link click inside
 * sends a click for the nearest item. Returns props for the section root.
 */
export const useSectionTracking = (sectionId, deps = []) => {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (sectionId == null || !root || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio >= 0.5) {
            trackShopEvent(sectionId, e.target.dataset.itemKey, 'view');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: [0.5] }
    );
    root.querySelectorAll('[data-item-key]:not([data-track-manual])').forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, ...deps]);

  const onClickCapture = useCallback(
    (e) => {
      if (sectionId == null) return;
      const a = e.target.closest?.('a');
      if (!a || !ref.current?.contains(a)) return;
      const item = a.closest('[data-item-key]');
      trackShopEvent(sectionId, item?.dataset.itemKey ?? '', 'click');
    },
    [sectionId]
  );
  return { ref, onClickCapture };
};

/* --------------------------------------------------- recently viewed */

const RECENT_KEY = 'lk-shop-recently-viewed';
const RECENT_MAX = 20;

export const readRecentlyViewed = () => {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(list) ? list.filter((p) => p && typeof p.slug === 'string' && p.title) : [];
  } catch {
    return [];
  }
};

/** Called by the product page on every visit: newest first, one entry per product, at most 20. */
export const rememberViewed = (p) => {
  if (!p?.slug || !p.title) return;
  try {
    const entry = {
      slug: p.slug,
      title: String(p.title).slice(0, 160),
      image: p.image || null,
      price: p.price != null ? Number(p.price) : null,
      mrp: p.mrp != null ? Number(p.mrp) : null,
      discountPercent: Number(p.discountPercent) || 0,
      brand: p.brand || null,
      at: Date.now(),
    };
    const list = [entry, ...readRecentlyViewed().filter((x) => x.slug !== p.slug)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    // Private mode / storage full: the row just won't show.
  }
};

/* ------------------------------------------------------------ misc */

export const usePrefersReducedMotion = () => {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return Boolean(reduced);
};

/** Milliseconds left until `iso`, ticking every second; null when `iso` is not a date. */
export const useCountdown = (iso) => {
  const end = iso ? new Date(iso).getTime() : NaN;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!Number.isFinite(end)) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [end]);
  return Number.isFinite(end) ? Math.max(0, end - now) : null;
};
